import { useMemo } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { Menu, Moon, Sun, Monitor, Clock, CheckSquare, Settings, Search, PanelRightOpen, PanelRightClose } from 'lucide-react'
import { useSettingsStore, type ThemeMode } from '@/stores/settingsStore'
import { useThemeOrchestratorStore } from '@/stores/themeOrchestratorStore'
import { useUIStore } from '@/stores/uiStore'
import { useAuthStore } from '@/stores/authStore'
import { useTaskStore } from '@/stores/taskStore'
import { IconButton, Tooltip, NotificationDropdown } from '@/components/ui'
import { ProgressRing } from '@/components/ui/ProgressRing'
import { WeatherIndicator } from '@/components/ui/WeatherIndicator'
import { OnlineUsersBadge } from '@/components/ui/OnlineUsersBadge'
import { formatDate } from '@/utils/format'

export function Header() {
  const navigate = useNavigate()
  const theme = useSettingsStore((state) => state.settings.theme)
  const colorPalette = useSettingsStore((state) => state.settings.colorPalette)
  const setTheme = useSettingsStore((state) => state.setTheme)
  const dailyGoal = useSettingsStore((state) => state.settings.dailyGoal)
  const activeOverlay = useThemeOrchestratorStore((state) => state.activeOverlay)
  const openSettingsModal = useUIStore((state) => state.openSettingsModal)
  const openCommandPalette = useUIStore((state) => state.openCommandPalette)
  const commandPaletteEnabled = useSettingsStore((state) => state.settings.commandPaletteEnabled)
  const openMobileMenu = useUIStore((state) => state.openMobileMenu)
  const isAuxPanelOpen = useUIStore((state) => state.isAuxPanelOpen)
  const toggleAuxPanel = useUIStore((state) => state.toggleAuxPanel)
  const user = useAuthStore((state) => state.user)
  const tasks = useTaskStore((state) => state.tasks)
  const location = useLocation()

  const isTasksPage = location.pathname === '/' || location.pathname === '/tasks'

  // M1: Memoize today's completion count
  const completedToday = useMemo(() => {
    if (!isTasksPage) return 0
    const today = formatDate(new Date())
    return tasks.filter(
      (t) => t.status === 'completed' && !!t.completedAt && formatDate(new Date(t.completedAt)) === today,
    ).length
  }, [tasks, isTasksPage])

  const getPageTitle = (): string => {
    const path = location.pathname
    if (path === '/' || path === '/tasks') return '작업'
    if (path.startsWith('/task/')) return '작업 상세'
    if (path === '/calendar') return '캘린더'
    if (path === '/groups') return '그룹'
    if (path.startsWith('/groups/')) return '그룹 상세'
    if (path === '/ai') return 'AI 코치'
    if (path === '/profile') return '분석'
    if (path === '/activity') return '활동 기록'
    return '작업'
  }

  const getPageDescription = (): string => {
    const path = location.pathname
    if (path === '/' || path === '/tasks') return '오늘의 우선순위와 진행 상황'
    if (path.startsWith('/task/')) return '작업의 세부 정보와 실행 흐름'
    if (path === '/calendar') return '시간의 흐름으로 정리한 일정'
    if (path === '/chat') return '대화와 협업 업데이트'
    if (path.startsWith('/groups')) return '함께 움직이는 프로젝트 공간'
    if (path === '/ai') return '생산성을 돕는 개인 어시스턴트'
    if (path === '/profile') return '나의 집중 패턴과 성장 기록'
    if (path === '/activity') return '최근 작업 변경 기록'
    return 'Moonwave 워크스페이스'
  }

  const cycleTheme = () => {
    const themeOrder: ThemeMode[] = ['light', 'dark', 'system', 'auto-time']
    const currentIndex = themeOrder.indexOf(theme)
    const nextIndex = (currentIndex + 1) % themeOrder.length
    setTheme(themeOrder[nextIndex])
  }

  const getThemeIcon = () => {
    switch (theme) {
      case 'light':
        return <Sun className="w-5 h-5" />
      case 'dark':
        return <Moon className="w-5 h-5" />
      case 'auto-time':
        return <Clock className="w-5 h-5" />
      default:
        return <Monitor className="w-5 h-5" />
    }
  }

  const themeLabels: Record<ThemeMode, string> = {
    light: '라이트 모드',
    dark: '다크 모드',
    system: '시스템 설정',
    'auto-time': '시간별 자동',
  }

  const getThemeBadgeInfo = () => {
    if (activeOverlay !== 'none') {
      switch (activeOverlay) {
        case 'survival': return { label: '⚡ 서바이벌 모드', colorClass: 'text-zinc-500 bg-zinc-100 dark:text-zinc-400 dark:bg-zinc-800' }
        case 'victory-gold': return { label: '✨ 타임캡슐', colorClass: 'text-yellow-700 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/30' }
        case 'aurora': return { label: '🌌 오로라', colorClass: 'text-fuchsia-600 bg-fuchsia-100 dark:text-fuchsia-400 dark:bg-fuchsia-900/30' }
        case 'weather-clear': return { label: '☀️ 맑음', colorClass: 'text-orange-600 bg-orange-100 dark:text-orange-400 dark:bg-orange-900/30' }
        case 'weather-cloudy': return { label: '☁️ 흐림', colorClass: 'text-slate-600 bg-slate-100 dark:text-slate-400 dark:bg-slate-900/30' }
        case 'weather-rain': return { label: '🌧️ 비', colorClass: 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30' }
        case 'weather-snow': return { label: '❄️ 눈', colorClass: 'text-sky-600 bg-sky-100 dark:text-sky-400 dark:bg-sky-900/30' }
        case 'weather-storm': return { label: '⛈️ 폭우', colorClass: 'text-indigo-600 bg-indigo-100 dark:text-indigo-400 dark:bg-indigo-900/30' }
        default: return null
      }
    }
    
    switch (colorPalette) {
      case 'ocean': return { label: '🌊 오션 블루', colorClass: 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30' }
      case 'rose': return { label: '🌹 로즈 핑크', colorClass: 'text-rose-600 bg-rose-100 dark:text-rose-400 dark:bg-rose-900/30' }
      case 'purple': return { label: '💜 퍼플', colorClass: 'text-purple-600 bg-purple-100 dark:text-purple-400 dark:bg-purple-900/30' }
      case 'forest': return { label: '🌲 포레스트', colorClass: 'text-emerald-600 bg-emerald-100 dark:text-emerald-400 dark:bg-emerald-900/30' }
      case 'default':
      default:
        return { label: '🎨 기본 색상', colorClass: 'text-indigo-600 bg-indigo-100 dark:text-indigo-400 dark:bg-indigo-900/30' }
    }
  }

  const badgeInfo = getThemeBadgeInfo()

  return (
    <header className="workspace-header sticky top-0 z-30 border-b transition-colors duration-200 el-header" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}> {/* --z-header + Edge Lighting */}
      <nav className="flex min-h-14 lg:min-h-[72px] items-center justify-between px-3 fold:px-2 sm:px-5 lg:px-7">
        {/* Left: Mobile menu + Logo */}
        <div className="flex min-w-0 items-center gap-3">
          <button
            onClick={openMobileMenu}
            className="lg:hidden flex min-h-[44px] min-w-[44px] -ml-2 items-center justify-center rounded-xl text-body hover-surface transition-colors"
            aria-label="메뉴 열기"
          >
            <Menu className="w-5 h-5 text-body" />
          </button>

          {/* 모바일: 로고 아이콘만 표시 (텍스트는 스크린리더 전용) */}
          <Link to="/" className="lg:hidden flex items-center">
            <div className="workspace-brand-mark w-9 h-9 rounded-xl flex items-center justify-center">
              <CheckSquare className="w-5 h-5 text-white" />
            </div>
            <span className="sr-only">To-Do List</span>
          </Link>

          {/* Desktop: page title */}
          <div className="hidden min-w-0 lg:block">
            <h2 className="truncate text-base font-semibold tracking-[-0.015em] text-heading">
              {getPageTitle()}
            </h2>
            <p className="mt-0.5 truncate text-meta text-caption">{getPageDescription()}</p>
          </div>
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Right: Actions */}
        <div className="flex items-center gap-1 sm:gap-1.5">
          {/* Search — 모바일 검색 도달성 (단축키 Ctrl+K와 동일하게 커맨드 팔레트 열기) */}
          {commandPaletteEnabled && (
            <Tooltip content="검색" placement="bottom">
              <IconButton
                plain
                color="secondary"
                onClick={openCommandPalette}
                aria-label="검색"
              >
                <Search className="w-5 h-5" />
              </IconButton>
            </Tooltip>
          )}

          {/* 동시접속자 배지 (로그인 시) */}
          <OnlineUsersBadge />

          {/* Progress ring (tasks page only) */}
          {isTasksPage && dailyGoal > 0 && (
            <Tooltip content={`오늘 ${completedToday}/${dailyGoal} 완료`} placement="bottom">
              <div className="mr-1 hidden sm:block">
                <ProgressRing completed={completedToday} total={dailyGoal} />
              </div>
            </Tooltip>
          )}

          {/* Weather indicator */}
          <WeatherIndicator />

          {/* User avatar */}
          {user && (
            <Tooltip content={user.displayName || user.email} placement="bottom">
              <button
                type="button"
                onClick={() => navigate('/profile')}
                className="mr-0.5 flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                aria-label="프로필 보기"
              >
                {user.photoURL ? (
                  <img
                    src={user.photoURL}
                    alt=""
                    className="w-8 h-8 rounded-full ring-2 ring-[var(--ring-focus)] shadow-sm object-cover"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-accent-primary flex items-center justify-center text-accent-primary text-sm font-bold ring-2 ring-[var(--ring-focus)] shadow-sm">
                    {user.displayName?.[0] || user.email?.[0] || '?'}
                  </div>
                )}
              </button>
            </Tooltip>
          )}

          {/* Notification bell */}
          <Tooltip content="알림" placement="bottom">
            <NotificationDropdown />
          </Tooltip>

          {/* Auxiliary panel toggle (desktop only) */}
          <div className="hidden lg:block">
            <Tooltip content={isAuxPanelOpen ? '패널 닫기' : '보조 패널'} placement="bottom">
              <IconButton
                plain
                color="secondary"
                onClick={toggleAuxPanel}
                aria-label={isAuxPanelOpen ? '보조 패널 닫기' : '보조 패널 열기'}
              >
                {isAuxPanelOpen ? <PanelRightClose className="w-5 h-5" /> : <PanelRightOpen className="w-5 h-5" />}
              </IconButton>
            </Tooltip>
          </div>

          {/* Settings button */}
          <div className="hidden sm:block">
            <Tooltip content="설정" placement="bottom">
              <IconButton
                plain
                color="secondary"
                onClick={openSettingsModal}
                aria-label="설정 열기"
              >
                <Settings className="w-5 h-5" />
              </IconButton>
            </Tooltip>
          </div>

          {/* Theme/Color Badge */}
          {badgeInfo && (
            <div className={`hidden 2xl:flex items-center px-2.5 py-1 text-meta font-semibold rounded-full cursor-default border border-transparent transition-all hover:opacity-80 ${badgeInfo.colorClass}`}>
              {badgeInfo.label}
            </div>
          )}

          {/* Theme toggle (hidden on mobile — accessible via hamburger menu) */}
          <div className="hidden md:block">
            <Tooltip content={themeLabels[theme]} placement="bottom">
              <IconButton
                plain
                color="secondary"
                onClick={cycleTheme}
                aria-label={`테마 변경 (현재: ${themeLabels[theme]})`}
              >
                {getThemeIcon()}
              </IconButton>
            </Tooltip>
          </div>
        </div>
      </nav>
    </header>
  )
}
